import {Injectable} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {TablelistStoreHandlerService} from './tablelist-store-handler.service';
import {Observable} from 'rxjs/Observable';
import {cloneDeep, find, findIndex, get, isEmpty} from 'lodash';
import {
  bufferCount,
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  finalize,
  flatMap,
  map,
  mergeMap,
  pluck,
  retry,
  share,
  switchMap,
  take,
  takeWhile,
  tap
} from 'rxjs/operators';
import { from } from 'rxjs';
import { of } from 'rxjs/observable/of';
import { ITablelistRequester, ITablelistStateListItem, ITablelistStateListItemConfig, ResourceTypes } from '../interfaces';
import { DataHandlerHelperServices } from './data-handler.helper.services';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { Filter } from '@ispm-shared/modules/common';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { SessionStorageService } from '@ispm/shared/services/session-storage.service';
import { SimpleSearchService } from './simple-search.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { SetIdentifierDataAction } from '../store/tablelist.actions';
import { PatientService } from '@ispm-client/modules/patient/services/patient.service';
import { DynamicSearchService } from './dynamic-search.service';

@Injectable()
export class TablelistDataHandlerService extends DataHandlerHelperServices {
  public set chunkSize(value) {
    this._chunkSize = value;
  }

  public get chunkSize() {
    return this._chunkSize;
  }

  public get type() {
    return this._type;
  }

  static readonly maxThreads = 4;
  private static REQUEST_DATA_SUFFIX: string = '_requestData_';

  public dataRequester$: Observable<ITablelistStateListItem>;

  private _chunkSize: number;
  private _defaultChunkSizePromise: Promise<void>;
  private _type = '';
  private currentPage = null;
  private dataAlreadyPulled = [];
  private dataRequester: Subject<ITablelistRequester> = new Subject<ITablelistRequester>();
  private defaultChunkSize: number;
  private haveSearch = false;
  private isFirst = false;
  private loadedData = false;
  private loadingSearch = false;
  private requestKey: string;
  private requesterParams: ITablelistRequester | null;
  private searchTerm = '';
  private startSearchIdx = 0;

  constructor(
    private _ispmHttpClient: IspmHttpClient,
    private _isgHttpClient: HttpInterceptorService,
    private simpleSearchService: SimpleSearchService,
    private tablelistStoreHandlerService: TablelistStoreHandlerService,
    private workflowService: WorkflowService,
    store: Store<IgenomicsState>,
    private patientService: PatientService,
    private dynamicsSearchService: DynamicSearchService,
  ) {
    super(store);
    this.setChannel();
    this.setDefaultChunkSize();
  }

  public mappingSortHotFix(sort) {
    const mappingSwitchKey = {
      'name.0.text': 'name',
      'identifier.0.value': 'identifier',
      'birthDate': 'birthdate',
      'openReports': 'openReportsCount'
    };

    if (mappingSwitchKey.hasOwnProperty(sort)) {
      sort = mappingSwitchKey[sort];
    }

    return sort;
  }

  public getConfigTemplate(resource: string = this._type , fetchType?: ResourceTypes): Observable<ITablelistStateListItem> {
    if (!fetchType) {
      fetchType = this.requesterParams
        ? this.requesterParams.resourceType
        : ResourceTypes.Manual; // configuration fetching is generic
    }

    return this.tablelistStoreHandlerService.getConfigTemplate({ configTemplateType: resource}).pipe(
      tap(data => {
        if (!(data && data.config)) {
          const key = this.buildCacheKey(`CONFIG`, resource);
          const cachedRequest = this.getOngoingRequestExisted(key);

          if (!cachedRequest) {
            const requestMeta = {
              resourceType: fetchType,
              configTemplateType: resource,
            };
            const request$ = this.tablelistStoreHandlerService.loadConfigurationTemplate(requestMeta).pipe(
              tap((config: any) => {
                if(config.resourceTypeCode && this.requesterParams) {
                  this.requesterParams.resourceType = config.resourceTypeCode
                }
              }),
              share()
            );

            this.cacheOngoingRequest(key, request$, request$.subscribe(), resource);
          }
        } else {
          const dynamicSearch = data.config.dynamicSearch;
          const res = data.config.resource;
          const associationItems = data.config.associationItems;
          const queryType = data.config.queryType;
          const simpleSearch = data.config.simpleSearch;

          sessionStorage.setItem(SessionStorageService.CURRENT_WORKLIST_DYNAMIC_SEARCH,
            JSON.stringify({ dynamicSearch, resource: res, associationItems, queryType, simpleSearch})
          );

          if (!this._chunkSize) {
            this._chunkSize = data.config.chunkSize;
          }
        }
      }),
      filter(config => config !== null)
    );
  }

  public initState(): void {
    this.clearRequestEmitter();
  }

  public clearRequestEmitter(): void {
    this.currentPage = null;
    this.tablelistStoreHandlerService.clearPagesIdRequests('');
    this.cleanUp();
  }

  public getDataListener() {
    return this.dataRequester$;
  }

  public requestEmitter(config: ITablelistRequester) {
    config = { ...config};

    if (config.start === 0 && config.resetIds) {
      this.cleanUp();
      this.loadedData = false;
      this.tablelistStoreHandlerService.lastIdsCall = false;
    }

    if (config.start === 0) {
      this.startSearchIdx = 0;
      this.threadsClear();
    }

    this.haveSearch = !!config.searchTerm;
    this.dataRequester.next(config);
  }

  public getQueryForExport(requester: ITablelistRequester) {
    const isClubbed = get(requester, 'dynamicSearch.clubbed');

    return this.getConfigTemplateOnlyTemplate(isClubbed).pipe(
      take(1),
      mergeMap((data) => {
        if (requester.searchTerm !== '') {
          let { patientValue, searchQuery, filters, associationItem} = this.extractedFilters(requester, data);

          return searchQuery.switchMap((patientIds: any) => {
            const __ret = this.setPatientAndFilter(patientIds, patientValue, filters, isClubbed, associationItem);
            patientValue = __ret.patientValue;
            const applyFilters = __ret.applyFilters;

            return this.getQuery(requester, data, applyFilters, patientValue);
          });
        }

        return this.getQuery(requester, data, []);
      }));
  }

  /**
   * this function allows to update a specific row by a specific ID
   * @param requester
   * @param id the row's data ID
   * @param updater a callback-function that is invoked to update the given item, if was found
   */
  public updateRowData(requester: ITablelistRequester, id: string, updater: (item: any) => any) {

    // get the relevant template's resource from the storage
    const resource$ = this.tablelistStoreHandlerService.getConfigTemplate({ configTemplateType: requester.configTemplateType})
      .pipe(
        take(1),
        tap(resource => {
          if (resource.loaded) {
            return of(resource); // if loaded, we will take the data as found currently
          }

          // we will open a stream
          return this.tablelistStoreHandlerService
            .getConfigTemplate({ configTemplateType: requester.configTemplateType})
            .pipe(
              takeWhile((updatedResource) => !updatedResource.loaded),
            );
        })

      );

    return resource$.pipe(
      map(resource => {

        if (requester.resource.toLowerCase() === 'worklistrow') {
          const itemIndex = findIndex(resource.data, item => item?.id === id);
          let dataItem = resource.data[itemIndex];
          if (dataItem) {
            dataItem = updater(dataItem);
            const requesterItemIndex = findIndex(requester.items, item => item?.id === id);
            if (requesterItemIndex >= 0) {
              requester.items[requesterItemIndex] = dataItem;
            }
          }
          return dataItem;

        } else {
          // get item with the requested ID
          let dataItem = resource.data[id];

        // get items from resource data
          if (!resource.items && resource.data) {
            resource.items = resource.data;
          }

          // find item in the resource's items collection
          let itemIndex = findIndex(resource.items, item => item?.id === id);

          // if item is found only in the resource's items collection:
          if (! dataItem && itemIndex >= 0) {
            dataItem = resource.items[itemIndex];
          }

          if (dataItem) {
            // call updater to update the data and get the updated item
            dataItem = updater(dataItem);

            // set updated item back to the data-model
            if (itemIndex >= 0 && resource.data[itemIndex]) {
              resource.data[itemIndex] = dataItem;
            }

            if (itemIndex >= 0) {
              resource.items[itemIndex] = dataItem;
            }

            // set also to the requester's 'items collection the updated item
            itemIndex = findIndex(requester.items, item => item?.id === id);
            if (itemIndex >= 0) {
            requester.items[itemIndex] = dataItem;
          }
          }

          return dataItem;
        }
      })
    );

  }

  private getConfigTemplateOnlyTemplate(isClubbed: boolean = false, isSimpleSearch: boolean = false) {
    if (isClubbed && !isSimpleSearch) {
      return this.tablelistStoreHandlerService.getConfigTemplateItem(this._type)
        .map((template) => {
          return { config: template, ids: [], amount: 0, data: []};
        });
    }

    return this.getConfigTemplate(this._type);
  }

  private getPage(requester: ITablelistRequester, isClubbed: boolean = false): Observable<any> {
    this._type = requester.configTemplateType;
    this.requesterParams = requester;

    if (requester.resourceType === ResourceTypes.Manual) {
      return this.getConfigTemplate();
    } else {
      return this.getConfigTemplateOnlyTemplate(isClubbed, this.simpleSearchService.isSimpleSearch(requester)).pipe(
        take(1),
        mergeMap((config) => {
          if (isClubbed) {
            if (config.data) {
              config.data = [...Object.values(config.data)];
            }

            if (this.chunkSize) {
              config.config.chunkSize = this.chunkSize;
            }
          }

          if (this.simpleSearchService.isSimpleSearch(config.config)) {
            return this.handleSimpleSearch(requester, config)
              .pipe(
                takeWhile(() => this.getRequestContextKey(requester) === this.requestKey)
              );
          } else {
            return this.handlePageIdsForSearchTerm(requester, config, isClubbed);
          }
        }),
        mergeMap((data: ITablelistStateListItem) => {

          if (requester.start === 0) {
            data.items = Array.from({ length: data.amount }); // []; // TODO For SimpleSearch we don't have data.amount!
          } else {
            data.items = requester.items || data.items; // we are appending to the existing results
          }

          data.items = data.items.filter(function (undefinedItems) {
            return undefinedItems !== undefined;
          });

          this.dataAlreadyPulled.push(...data.items);

          if (this.simpleSearchService.isSimpleSearch(data.config)) {
            this.simpleSearchService.adaptDataToWorklist(data);
            data.items.push.apply(data.items, data.data);

            return Observable.of(data);
          }

          if (requester.searchTerm !== '') {
            if (isClubbed) {
              const needsFlat = get(data.config, 'dynamicSearch.dataQuery.resourceNeedsFlatten');

              if (needsFlat) {
                data.data = this.flattenPageData(data.data, needsFlat, requester.resource);
              }

              data.items.push.apply(data.items, data.data);

              return Observable.of(data);
            }

            return this.searchByTerm(requester, data.config, data);
          } else {
            if (isClubbed) {
              const needsFlat = get(data.config, 'dynamicSearch.dataQuery.resourceNeedsFlatten');

              if (needsFlat) {
                data.data = this.flattenPageData(data.data, needsFlat, requester.resource);
              }

              data.items.push.apply(data.items, data.data);
              // data.items.splice(requester.start,data.data.length, ...data.data);

              return Observable.of(data);
            }

            return this.handlePageData(requester, requester.start, data.config, data)
              .pipe(
                takeWhile(() => this.getRequestContextKey(requester) === this.requestKey),
                map((payload) => {
                  payload.items = payload.items || [];

                  // Note: this code was moved to handlePageData() internal to handle all search cases
                  if (get(payload, 'config.dynamicSearch.dataQuery.resourceNeedsFlatten')) {
                    const resourcesNeedFlatten = payload.config.dynamicSearch.dataQuery.resourceNeedsFlatten;
                    payload.resolvedIds = this.flattenPageData(payload.resolvedIds, resourcesNeedFlatten, get(payload, 'config.resource'));
                  }

                  payload.items.push.apply(payload.items, payload.resolvedIds);
                  delete payload.resolvedIds;

                  return payload;
                })
              );
          }
        }),
        map(data => {
          if (data.hasOwnProperty('totalEntities')) {
            delete data.totalEntities;
          }
          return data;
        }),switchMap(data => {
          let dataQuery = null;
          let result = {"initialData": data, "linkData": null};
          if (data.amount > 0) {
            dataQuery = this.extractLinkedPatientResourceIds(data)
          }
          if(dataQuery) {
            return this.dynamicsSearchService.sendPureRequest(dataQuery, data.config.resource, 'clubbed').pipe(
              map(additionalWorkListRowItems =>{
                if(this.haveSearch){
                  additionalWorkListRowItems.data.forEach(item =>{
                    let newData = [item]
                    if (get(data, 'config.dynamicSearch.dataQuery.resourceNeedsFlatten')) {
                      const resourcesNeedFlatten = data.config.dynamicSearch.dataQuery.resourceNeedsFlatten;
                      newData = this.flattenPageData(newData, resourcesNeedFlatten, get(data, 'config.resource'));
                    }
                    data.ids.push(newData[0].id)
                    data.data.push(newData[0])
                    data.items.push(newData[0])
                    })
                    return result;
                } else {
                  result.linkData = additionalWorkListRowItems;
                  return result;
                }
            }));
          }
          else {
            return of(result);
        }
      }),switchMap((data: any) => {
        let aggIdentifierMap = {}
        if(data.initialData.data.length > 0) {
          //aggIdentifierMap = this.getAggIdentifierMapPerPatient(data)
          aggIdentifierMap = this.getAggregateDataPerPatient(data, data.initialData.config)
          }
          this.updateStore(data.initialData.config.type , aggIdentifierMap);
          return of(data.initialData)
        })
      );
    }
  }

  private extractLinkedPatientResourceIds(data) {
    let dataQuery = null;
    let keyValuesObject = this.patientService.getPatientKeysFromResource(data.data[0])
    let otherKnownIdsKey = keyValuesObject.otherKnownIdsKey
    let patientIdKey = keyValuesObject.patientIdKey
    let patientLinks = this.patientService.getUniqueLinkPatientIdsStr(data.data, patientIdKey, otherKnownIdsKey)
    // let dataQuery = this.getLinkedWorkListRowQueryUrl(data, patientLinks)
    // return dataQuery
    if(patientLinks.length > 0) {
      const patientFilter = new Filter(
        data.config.resource,
        '=',
        patientLinks,
        "{".concat(patientIdKey,"}"),
        "searchAggDynamicIDX"
      );
      dataQuery = cloneDeep(data.config.dynamicSearch.dataQuery)
      dataQuery.filters.push(patientFilter)
      dataQuery.target._count = data.config.chunkSize
    }
    return dataQuery
  }

  private getLinkedWorkListRowQueryUrl(data, patientLinks) {
    let resourceType = data.data[0].resourceType
    let fieldsList: any = ""
    let url = ""
    let keyValuesObject = this.patientService.getPatientKeysFromResource(data.data[0])
    const patientFilter = new Filter(
      data.config.resource,
      '=',
      patientLinks,
      keyValuesObject.patientIdKey,
      "searchAggDynamicIDX"
    );
    let dataQuery = cloneDeep(data.config.dynamicSearch.dataQuery)
    dataQuery.filters.push(patientFilter)
    return dataQuery
    this.dynamicsSearchService.sendPureRequest(dataQuery, data.config.resource, 'clubbed')
    .pipe(
      map((resp: any) => {
      return resp.data;
    }));
    // if ( resourceType === "WorkListRow" ) {
    //     fieldsList = data.config.dynamicSearch.dataQuery.target._fieldslist
    //     fieldsList = fieldsList.replaceAll('#','%23')
    //     url = `entity-tree/WorkListRow?WLRDOPatient=${patientLinks}&WLRDOtesttype=${data.data[0]['DiagnosticOrder#testType']}&_fieldslist=${fieldsList}`
    // } else if (resourceType === "UIPractWorkListRow") {
    //    url = `entity-tree/UIPractWorkListRow?patientId=${patientLinks}&AppConfiguration=${data.data[0]['AppConfiguration']}`
    // } else if (resourceType === "Patient") {
    //     if(data.config.simpleSearch) {
    //       fieldsList = data.config.simpleSearch.fieldslist
    //     } else {
    //       fieldsList = "id,name.0.text,identifier.0.value,gender,birthDate,aggIdentifier,otherKnownIds,Organization,active"
    //     }
    //   url = `entity-tree/patient?_id=${patientLinks}&_fieldslist=${fieldsList}`
    // }
    // return url
  }

  private getAggIdentifierMapPerPatient(workListRowListData){
    let workListRowList = workListRowListData.initialData.data;
    let searchedData = workListRowListData.searchedData;

    let keyValuesObject = this.patientService.getPatientKeysFromResource(workListRowList[0])
    let otherKnownIdsKey = keyValuesObject.otherKnownIdsKey
    let patientIdKey = keyValuesObject.patientIdKey
    let aggIdentifierKey = keyValuesObject.aggIdentifierKey
    let openReportKey = keyValuesObject.openReport
    let lungRADSScoreKey = keyValuesObject.lungRADSScore

    let patientIdentifierMap = {}
    let resourceSet = new Set()
    for(let resource of workListRowList) {
      if (!resourceSet.has(resource[patientIdKey])) {
        let patientLinks = this.patientService.getPatientLinksFromResource(resource, otherKnownIdsKey)
        let patientIdGroup = [resource[patientIdKey], ...patientLinks]
        let identifierList = [resource[aggIdentifierKey]]
        let openReportList = resource[openReportKey]!=null?[resource[openReportKey]]:[];
        let lungRADSScoreList = resource[lungRADSScoreKey]!=null?[resource[lungRADSScoreKey]]:[];

        for (let index = 1; index < patientIdGroup.length; index++) {
          let patientId = patientIdGroup[index]
          let resource = workListRowList.find(resource => resource[patientIdKey] == patientId)
          if((!resource) && searchedData) {
            resource = searchedData.find(resource => resource[patientIdKey] == patientId)
          }

          if(resource) {
            identifierList.push(resource[aggIdentifierKey]);

            if(patientLinks?.length > 0){
              if(resource[openReportKey]!=null){
                openReportList.push(resource[openReportKey]);
              }
              if(resource[lungRADSScoreKey]!=null){
                lungRADSScoreList.push(resource[lungRADSScoreKey]);
              }
            }
          }
        }
        let mergedIdentifiers = this.patientService.mergeAggIdentifiers(identifierList);
        let mergedOpenReportAndScore = {}, mergedLungRADSScore = {};

        if(patientLinks?.length > 0){
          mergedOpenReportAndScore = this.patientService.mergeOpenReportAndScore(openReportList)
          mergedLungRADSScore = this.patientService.mergeLungRADSScore(lungRADSScoreList)
        }

        patientIdGroup.forEach(patientId => {
            patientIdentifierMap[patientId] = {"addIdentifier": mergedIdentifiers, "DiagnosticOrder#Patient#Events#Open": mergedOpenReportAndScore, "DiagnosticOrder#Observations#RADIOLOGY.LDCT.LUNG#last": mergedLungRADSScore }
            resourceSet.add(patientId)
          })
      }
    }
    return patientIdentifierMap;

  }

  private getAggregateDataPerPatient(workListRowListData, config){
    let workListRowList = workListRowListData.initialData.data;
    let linkData = workListRowListData.linkData?.data;

    let keyValuesObject = this.patientService.getPatientKeysFromResource(workListRowList[0])
    let otherKnownIdsKey = keyValuesObject.otherKnownIdsKey
    let patientIdKey = keyValuesObject.patientIdKey
    let aggIdentifierKey = keyValuesObject.aggIdentifierKey

    let patientIdentifierMap = {}
    let resourceSet = new Set()

    let aggregatedList;
    let aggregateConfig = []

    try{
      config.table.forEach(config=> {
        let aggregationKey = config.keys.find(key => key.dataAggregation != null);
        if(aggregationKey){
          aggregateConfig.push(aggregationKey);
        }
      })

      for(let resource of workListRowList) {
        if (!resourceSet.has(resource[patientIdKey])) {
          aggregatedList =new Object();

          let patientLinks = this.patientService.getPatientLinksFromResource(resource, otherKnownIdsKey)
          let patientIdGroup = [resource[patientIdKey], ...patientLinks]
          let identifierList = [resource[aggIdentifierKey]]
          aggregateConfig.forEach(element => {
            aggregatedList[element.dataAggregation.key]=resource[element.dataAggregation.key] != null? [resource[element.dataAggregation.key]] : [];
          });

          for (let index = 1; index < patientIdGroup.length; index++) {
            let patientId = patientIdGroup[index]
            let resource = workListRowList.find(resource => resource[patientIdKey] == patientId)
            if((!resource) && linkData) {
              resource = linkData.find(resource => resource[patientIdKey] == patientId)
            }
            if(resource) {
              identifierList.push(resource[aggIdentifierKey]);

              aggregateConfig.forEach(element => {
                if(resource[element.dataAggregation.key] != null){
                  aggregatedList[element.dataAggregation.key].push(resource[element.dataAggregation.key]);
                }
              });
            }
          }
          let mergedIdentifiers = this.patientService.mergeAggIdentifiers(identifierList);

          patientIdGroup.forEach(patientId => {
              patientIdentifierMap[patientId] = {"aggIdentifier": mergedIdentifiers}

              aggregateConfig.forEach(configItem => {
                let mergedItem = this.patientService.mergePatientData(aggregatedList[configItem.dataAggregation.key], configItem.dataAggregation.type, configItem.dataAggregation.keyForSingleSelection);
                let _resource = workListRowList.find(resource => resource[patientIdKey] == patientId)
                if(_resource){
                _resource[configItem.dataAggregation.key] = mergedItem;
                }
              });
              resourceSet.add(patientId)
            })
        }
      }
    } catch(err){
      console.log('error while aggregating data in worklist ', err);
    }
    return patientIdentifierMap;
  }

private updateStore(configTemplateType, aggIdentifierMap) {
  this.dispatch(new SetIdentifierDataAction({
          configTemplate: configTemplateType,
          aggIds: aggIdentifierMap
    }))
}

  private getSortedDataArrayFromIds(data: { [id: string]: any}, ids: string[]) {
    const arr = [];
    ids.forEach(id => {
      if (data[id]) {
        arr.push(data[id]);
      }
    });
    return arr;
  }

  private handleSimpleSearch(config: ITablelistRequester, data) {
    data = cloneDeep(data);

    const sortBy = this.sortBySimpleSearch(config, data);
    const pageToLoad: number = +this.currentPage;

    return this.tablelistStoreHandlerService.requestNextPageIdsSimpleSearch(
      data.config.simpleSearch,
      config.searchTerm,
      config.configTemplateType,
      pageToLoad,
      sortBy.sortByField,
      sortBy.sortByOrder,
      data.config.maxItemsPerPage,
      config.queryOrigin,
      config.associationItems
    )
      .pipe(takeWhile(() => this.getRequestContextKey(config) === this.requestKey))
      .map((response: any) => {
        data.data = response;
        this.currentPage = pageToLoad;

        return data;
      });
  }

  private handlePageIds(config: ITablelistRequester, data, filters = [], isClubbed: boolean = false, patientIds?: string) {
    data = cloneDeep(data);

    if ((config.resetIds) || isClubbed) {
      const sortBy = this.getSortByObject(config, data);
      let reset = true;
      const idQuery = this.getIdQuery(config, filters);
      const sortPath = config.dynamicSearch.hasOwnProperty('sortPath') ? get(config.dynamicSearch, 'sortPath') : true;

      if (!isClubbed) {
        if (this.requesterParams.resourceType === ResourceTypes.Generic) {
          this.tablelistStoreHandlerService.requestTotalIds(
            this.requesterParams.resourceType,
            this.requesterParams.configTemplateType,
            idQuery,
            config.resource,
            sortPath,
            data.config.associationItems
          )
            .publish()
            .connect();
        }

        return Observable.fromPromise(this._defaultChunkSizePromise)
          .pipe(
            take(1),
            switchMap(_ => this.tablelistStoreHandlerService.requestPagesIds(
                config.resourceType,
                config.configTemplateType,
                data.config.resourceQuery,
                1,
                sortBy.sortByField,
                sortBy.sortByOrder,
                this.chunkSize ? this.chunkSize : (data.config.serverIdsChunkSize || this.defaultChunkSize),
                this.haveSearch,
                idQuery,
                sortBy.sortByType,
                config.resource,
                config.offset,
                sortPath,
                data.config.associationItems,
                data.config.adaptableFilters,
                isClubbed
              )
            ),
            take(1),
            map((requesterIds: any) => {
              if (reset) {
                reset = false;
                data.ids = requesterIds.ids;
                if (isClubbed) {
                  // data.data= lodashValues(requesterIds.data);
                  data.data = requesterIds.data;
                }
              }
              return data;
            })
          );
      } else {
        return this.tablelistStoreHandlerService.requestNextPageIds(
          config.resourceType,
          config.configTemplateType,
          data.config.resourceQuery,
          this.currentPage ? this.currentPage + 1 : 1,
          sortBy.sortByField,
          sortBy.sortByOrder,
          this.chunkSize ? this.chunkSize : (data.config.serverIdsChunkSize || this.defaultChunkSize),
          this.haveSearch,
          idQuery,
          sortBy.sortByType,
          config.resource,
          sortPath,
          data.config.associationItems,
          data.config.adaptableFilters,
          true,
          patientIds
        )
          .pipe(
            takeWhile(() => this.getRequestContextKey(config) === this.requestKey)
          )
          .map((response: any) => {
            reset = false;

            if (data.ids.length === 0) {
              data.ids = response.resources_ids;
              data.data = response.resources_data;
              data.amount = response.totalEntities;
            } else {
              data.ids.push.apply(data.ids, response.resources_ids);
              let currentDataValues = data.data || [];
              // const receivedDataValues = lodashValues(response.resources_data);
              const receivedDataValues = response.resources_data;
              currentDataValues = currentDataValues.concat(receivedDataValues);
              data.data = currentDataValues;
            }

            this.currentPage = response.currentPage;

            return data;
          });
      }
    } else {
      if (config.ids && config.ids.length > 0) {
        data.ids = config.ids;
      }

      return of(data);
    }
  }

  private getIdQuery(config: ITablelistRequester, filters) {
    if (config.dynamicSearch) {
      let idQuery = cloneDeep(config.dynamicSearch.idQuery);

      if (config.dynamicSearch.clubbed) {
        idQuery = cloneDeep(config.dynamicSearch.dataQuery);
      }

      if (filters.length > 0) {
        idQuery['filters'] = isEmpty(idQuery['filters']) ? filters : idQuery['filters'].concat(filters);
      }

      return idQuery;
    }

    return null;
  }

  private getFilterMissingIds(idsList, dictionary: any) {
    const ids = [];
    idsList.forEach(id => {
      if (!(dictionary.hasOwnProperty(id) && dictionary[id])) {
        ids.push(id);
      }
    });
    return ids;
  }

  private searchByTerm(requester: ITablelistRequester, config: ITablelistStateListItemConfig, data: any) {
    data.endSearch = false;
    this.loadingSearch = true;

    const displayColumns = config.table;
    const searchTerm = requester.searchTerm.toLowerCase();
    const ids$ = this.tablelistStoreHandlerService.getConfigTemplate({ configTemplateType: requester.configTemplateType})
      .pipe(
        filter(resource => !!resource),
        take(1),
        flatMap((resource) => {
          if (resource.loaded) {
            return of(resource); // if loaded, we will take the data as found currently
          } else {
            // we will open a stream
            return this.tablelistStoreHandlerService
              .getConfigTemplate({ configTemplateType: requester.configTemplateType})
              .pipe(
                takeWhile((updatedResource) => !updatedResource.loaded || this.startSearchIdx < updatedResource.ids.length),
              );
          }
        }),
        tap((resource) => {
          this.loadingSearch = !resource.loaded || this.startSearchIdx < resource.ids.length;
          data.endSearch = !this.loadingSearch;
        }),
        takeWhile(() => this.searchTerm && this.searchTerm.toLowerCase() === searchTerm),
        map(resource => resource.ids),
        distinctUntilChanged()
      );

    return ids$.pipe(
      flatMap(ids => {
        const currentStartSearchIdx = this.startSearchIdx;
        this.startSearchIdx = ids.length;
        return ids.slice(currentStartSearchIdx, ids.length);
      }),
      bufferCount(this.chunkSize),
      tap(() => requester.onSearchEndEvent.emit(false)),
      concatMap((ids: string[]) => {
        return this.handlePageData(requester, 0, config, data, ids)
          .pipe(pluck('resolvedIds'),
            map((resolvedIds) => {
              // Flatten data
              if (get(requester, 'dynamicSearch.dataQuery.resourceNeedsFlatten')) {
                const resourcesNeedFlatten = requester.dynamicSearch.dataQuery.resourceNeedsFlatten;
                resolvedIds = this.flattenPageData(resolvedIds, resourcesNeedFlatten, requester.resource);
              }
              return resolvedIds;
            })
          );
      }),
      concatMap((filteredResults: any) => {
        return Observable.of(filteredResults)
          .pipe(
            mergeMap(subChunk => {
              return new Promise(resolve => {
                const worker = new Worker('/workers-transpiled/filterTablelistData.js');
                const results = [];
                worker.onmessage = msg => {
                  const item = msg.data;
                  if (item !== '-1') {
                    results.push(subChunk[item]);
                  } else {
                    worker.terminate();
                    resolve(results);
                  }
                };
                // console.log('preMessage ', {dataToFilter: subChunk, displayColumns, searchTerm});
                worker?.postMessage({ dataToFilter: subChunk, displayColumns, searchTerm});
                this.threads.push(worker);
              }).then((results: any[]) => {
                data.items = data.items || [];
                if (results.length > 0) {
                  data.items.push.apply(data.items, results);
                }
                return data;
              });
            }, TablelistDataHandlerService.maxThreads)
          );
      }),
      bufferCount(1), // emit each value independent
      map(item => item[0]),
      catchError(err => {
        console.error('Search Term Table list Error', err);
        switch (err && err.code) {
          case -1:
            return Observable.of([]);
          default:
            return Observable.throw(err);
        }
      }),
      finalize(() => {
        this.threadsClear();
        requester.onSearchEndEvent.emit(true);
      }),
    );

  }

  private handlePageData(requesterConfig: ITablelistRequester, start: number = 0, config: ITablelistStateListItemConfig, data: ITablelistStateListItem, ids?: string[]) {
    if (!ids) {
      ids = data.ids.slice(start, start + this.chunkSize);
    }

    let fieldList = '';

    config.table.map(item => {
      fieldList += item.keys.map(itemKey => itemKey['key']).join() + ',';
    });

    fieldList = fieldList.slice(0, -1);
    this.tablelistStoreHandlerService.setFieldList(fieldList);
    const missingIds: Array<string> = this.getFilterMissingIds(ids, data.data);
    let data$: Observable<any>;

    if (missingIds.length === 0) {
      data$ = of(data);
    } else {
      data$ = from(missingIds).pipe(
        bufferCount(this.chunkSize),
        mergeMap(ids => {
          const cachedKey = this.buildCacheKey(requesterConfig.resourceType, requesterConfig.configTemplateType, TablelistDataHandlerService.REQUEST_DATA_SUFFIX, ids[0]);
          let cachedRequest = this.getOngoingRequestExisted(cachedKey);
          if (!cachedRequest) {

            requesterConfig.dynamicSearch.multipleSearch = get(requesterConfig,'useMultipleSearch', false);

            const dataRequest = this.tablelistStoreHandlerService.requestPageData(requesterConfig.resourceType, requesterConfig.configTemplateType, ids,
              requesterConfig.dynamicSearch ? requesterConfig.dynamicSearch.dataQuery : null, requesterConfig.resource, requesterConfig.associationItems, requesterConfig.adaptableFilters)
              .pipe(
                retry(2),
                share()
              )
              .publish();
            cachedRequest = this.cacheOngoingRequest(cachedKey, dataRequest, dataRequest.connect());
          }
          return cachedRequest.request;
        }, TablelistDataHandlerService.maxThreads),
        map((items: { items: any }) => {
          Object.assign(data.data, items.items);
          return data;
        })
      );
    }

    return data$.pipe(
      map((d) => {
        d.resolvedIds = this.getSortedDataArrayFromIds(d.data, ids);

        return d;
      })
    );
  }

  private extractedFilters<A>(config: ITablelistRequester, data) {
    const searchTerm: string = (config.searchTerm as string).replace(new RegExp(' ', 'g'), '%25'); // '%25' allow searching on partial string
    const patientValue = '';
    let searchQuery;
    const filters = [];
    const associationItem = find(get(data.config, 'associationItems'), { 'name': 'searchByFilter'});
    searchQuery = of(associationItem);

    if (associationItem) {
      filters.push(new Filter(associationItem.resource, associationItem.operator, searchTerm, associationItem.path, associationItem.type));
    } else {
      searchQuery = this._isgHttpClient.get('patient',
        {
          '_fieldslist': 'id,name,identifier,mrn',
          '_fsort': 'family',
          active: true,
          combinedParams: 'name,mrn',
          searchValue: `${searchTerm}`
        });
    }

    return { patientValue, searchQuery, filters, associationItem};
  }

  private getQuery(requester, data, filters, patientValue?: string) {
    const clonedData = cloneDeep(data);
    const uniqueFieldsList = this.getUniqueFieldList(clonedData);

    // SimpleSearch
    if (this.simpleSearchService.isSimpleSearch(requester)) {
      clonedData.config.simpleSearch.fieldslist = uniqueFieldsList.join(',');
      clonedData.config.simpleSearch.chunkSize = clonedData.config.exportData.maxExportedItems;
      const { sortByField, sortByOrder} = this.sortBySimpleSearch(requester, clonedData);

      clonedData.config.simpleSearch.multipleSearch = get(requester, 'useMultipleSearch', false);
      return this.simpleSearchService.getQuery(clonedData.config.simpleSearch, requester.queryOrigin, requester.searchTerm, 0,
        sortByField, sortByOrder, filters, false, '', requester.associationItems);
    }

    const idQuery = this.getIdQuery(requester, filters);
    idQuery.target._fieldslist = uniqueFieldsList.join(',');

    // DynamicSearch
    const sortBy = this.getSortByObject(requester, data);

    return this.tablelistStoreHandlerService.getQuery(
      requester.resourceType,
      requester.configTemplateType,
      1,
      sortBy.sortByField,
      sortBy.sortByOrder,
      data.config.exportData.maxExportedItems,
      idQuery,
      sortBy.sortByType,
      requester.resource,
      sortBy.sortPath,
      data.config.associationItems,
      data.config.adaptableFilters,
      patientValue
    );
  }

  private sortBySimpleSearch(config: ITablelistRequester, data) {
    const defSortByField = get(data, 'config.sortByField', 'id');
    const defSortByOrder = get(data, 'config.sortByOrder', 1);
    const sortFieldName: string = this.simpleSearchService.getFilterParameterNameByNameInWorklist(config.sortByField, config.simpleSearch.simpleSearchWorklistMapping);
    const sortByField = get(config, 'sortByField', null) !== null ? sortFieldName : defSortByField;
    const sortByOrder = get(config, 'sortByOrder', null) !== null ? config.sortByOrder : defSortByOrder;

    return { sortByField, sortByOrder};
  }

  private getUniqueFieldList(data) {
    const fieldsList = [];
    data.config.exportData.exportResources.forEach(exportResources => {
      exportResources.resources.forEach(resources => {
        fieldsList.push(resources.resourceIdentifier);
      });
    });

    return [...new Set(fieldsList)];
  }

  private getSortByObject(config, data) {
    const sortPath = config.dynamicSearch.hasOwnProperty('sortPath') ? get(config.dynamicSearch, 'sortPath') : true;

    const defSortByField = get(data, 'config.sortByField', '_id');
    const defSortByOrder = get(data, 'config.sortByOrder', 1);
    const defSortByType = get(data, 'config.sortByType', 'string');
    const sortByField = get(config, 'sortByField', null) !== null ? config.sortByField : defSortByField;
    const sortByOrder = get(config, 'sortByOrder', null) !== null ? config.sortByOrder : defSortByOrder;
    let sortByType = get(find(data.config.table, ['keys.0.key', sortByField]), 'keys.0.type', null);
    sortByType = sortByType !== null ? sortByType : defSortByType;

    return {
      sortByField, sortByOrder, sortByType, sortPath
    };
  }

  private cleanUp() {
    this.cancelPreviousRequests(); // we clear all requests if there are any
    this.requests.clear();
    this.threadsClear();
  }

  private threadsClear() {
    this.threads.forEach(thread => thread.terminate());
    this.threads.length = 0;
  }

  private setChannel() {
    this.dataRequester$ = this.dataRequester
      .asObservable()
      .pipe(
        tap((config) => {
          this.requestKey = this.getRequestContextKey(config);
        }),
        switchMap((data) => of(data)),
        filter((parameters: ITablelistRequester) => parameters.configTemplateType !== ''),
        tap((parameters: ITablelistRequester) => {
          if (!!parameters.searchTerm) {
            this.isFirst = true;
          }

          this.searchTerm = parameters.searchTerm;
          this.isFirst = !parameters.start;
          this.requesterParams = parameters;
        }),
        tap((data) => {
          const isClubbed = get(data, 'dynamicSearch.clubbed');

          if (this.requesterParams.resetIds && isClubbed && !this.simpleSearchService.isSimpleSearch(data)) {
            this.currentPage = 0;
            data.ids = [];
          }

          this.currentPage = Math.floor(data.start / this.chunkSize);

          if (this.requesterParams.resourceType === ResourceTypes.Generic) {
            if ((!this.requesterParams.resetIds && this.haveSearch === false) && (!(this.currentPage))) {
              this.tablelistStoreHandlerService.clear(this.requesterParams.resourceType, this.requesterParams.configTemplateType).subscribe();
            } else if ((this.requesterParams.resetIds) && (!(this.currentPage))) {
              this.tablelistStoreHandlerService.clearIds(this.requesterParams.resourceType, this.requesterParams.configTemplateType, isClubbed).subscribe();
            }
          }
        }), // TODO need by default clubbing
        switchMap((requester) =>
          this.getPage(requester, get(requester, 'dynamicSearch.clubbed'))
            .catch((err) => {
              // This catch MUST be used. If not, when there is an error in one of the requests (error 500),
              // the observable is unsubscribes and the worklist does not send data fetch requests anymore.
              // return Observable.of(err) must be returned. If throw will be used, the error won't be caught in worklist.component->listenToData
              return Observable.of(err);
            })
        ),
        finalize(() => {
          this.cleanUp();
        })
      );
  }

  private setDefaultChunkSize() {
    this._defaultChunkSizePromise = this.workflowService.getDefaultChunkSize().then((chunkSize) => {
      this.defaultChunkSize = chunkSize.body.defaultChunkSize;
    });
  }

  private getRequestContextKey(requester: ITablelistRequester) {
    return [
      requester.configTemplateType,
      requester.resourceQuery,
      requester.sortByField,
      requester.sortByOrder,
      requester.searchTerm
    ].join('__');
  }

  private handlePageIdsForSearchTerm(requester, config, isClubbed: boolean = false) {
    if (requester.searchTerm !== '') {
      let { patientValue, searchQuery, filters, associationItem} = this.extractedFilters(requester, config);

      return searchQuery.switchMap((patientIds: any) => {
        const __ret = this.setPatientAndFilter(patientIds, patientValue, filters, isClubbed, associationItem);
        patientValue = __ret.patientValue;

        return this.handlePageIds(requester, config, __ret.applyFilters, isClubbed, patientValue);
      });
    } else {
      return this.handlePageIds(requester, config, [], isClubbed, null)
        .pipe(
          takeWhile(() => this.getRequestContextKey(requester) === this.requestKey)
        );
    }
  }

  private setPatientAndFilter(patientIds: any, patientValue: string, filters: any[], isClubbed: boolean, associationItem: { name: string }) {
    if (!isEmpty(get(patientIds, 'body'))) {
      for (const item of patientIds.body) {
        patientValue = patientValue + item.id + ',';
      }

      patientValue = patientValue.slice(0, -1);
      filters.push(new Filter('Patient', '=', patientValue, '_id', 'identifier'));
    }

    let applyFilters = [];

    if (isClubbed) {
      if (associationItem) {
        applyFilters = filters;
        patientValue = null;
      }
    } else {
      applyFilters = filters;
      patientValue = null;
    }

    return { patientValue, applyFilters};
  }

  private flattenPageData(resolvedIds, resourcesNeedFlatten = [], resource) {
    if (resolvedIds) {
      if (resource.toLowerCase() === 'worklistrow') {
        resolvedIds.map((val, index) => {
          resourcesNeedFlatten.forEach(flattenRes => { // Maybe add it here AMNON
            if (get(val, 'flattenRes')) {
              resolvedIds[index][flattenRes] = Object.assign({ }, ...get(val, 'flattenRes'));
            }
          });
          // TODO
          // temporary code until script/Mapper so many places code is expecting below fields thats why hard coding
          // Configuration is tight coupled with code mostly in PNC
          resolvedIds[index]['WorkflowRequest'] = resolvedIds[index]['DiagnosticOrder#WorkflowRequest'];
          resolvedIds[index]['WorkflowRequest']['revisions_-1_id'] = resolvedIds[index]['DiagnosticOrder#WorkflowRequest']['latestRevision'];
          resolvedIds[index]['WorkflowRequest']['revisions_-1_name'] = resolvedIds[index]['DiagnosticOrder#WorkflowRequest']['latestRevisionName'];
          resolvedIds[index]['WorkflowRequest']['revisions_-1_status'] = resolvedIds[index]['DiagnosticOrder#WorkflowRequest']['latestRevisionStatus'];
          resolvedIds[index]['WorkflowRequest_id'] = resolvedIds[index]['DiagnosticOrder#WorkflowRequest']['id'];
          resolvedIds[index]['subject'] = resolvedIds[index]['DiagnosticOrder#subject'];
          resolvedIds[index]['Patient'] = { id: resolvedIds[index]['DiagnosticOrder#subject'], name_0_text: resolvedIds[index]['DiagnosticOrder#Patient#name']};
          resolvedIds[index]['performer'] = resolvedIds[index]['DiagnosticOrder#performer'];
          resolvedIds[index]['lastRevision'] = resolvedIds[index]['DiagnosticOrder#WorkflowRequest']['latestRevision'];
          resolvedIds[index]['worklistrowid'] = resolvedIds[index]['id'];
          resolvedIds[index]['id'] = resolvedIds[index]['DiagnosticOrder'];
          resolvedIds[index]['meta'] = resolvedIds[index]['DiagnosticOrder#meta'];
          resolvedIds[index]['manualTags'] = resolvedIds[index]['DiagnosticOrder#manualTags'];
          resolvedIds[index]['clinicalDate'] = resolvedIds[index]['DiagnosticOrder#clinicalDate'];
        });

      } else {
        resolvedIds.map((val, index) => {
          resourcesNeedFlatten.forEach(flattenRes => {
            resolvedIds[index][flattenRes] = Object.assign({ }, ...val[flattenRes]);
          });
        });
      }

    }

    return resolvedIds;
  }
}
